import React, { useEffect, useRef } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from 'helpers/theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import classNames from 'classnames';
import {
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faTriangleExclamation
} from '@fortawesome/pro-solid-svg-icons';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';

const useStyles = createUseStyles((theme: Theme) => ({
  '@keyframes animateIn': {
    from: {
      transform: 'translate(500px, 0)',
      opacity: 1
    },
    to: {
      transform: 'translate(0px, 0px)',
      opacity: 1
    }
  },
  base: {
    zIndex: 100000,
    animationName: '$animateIn',
    animationDuration: '0.5s',
    background: 'white',
    boxShadow: '0px 4px 8px #0000000f',
    borderRadius: 12,
    border: '1px solid #E0E3E7',
    padding: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '17px',
    width: 500
  },
  info: {
    background: '#e5effa',
    border: '1px solid #abcdf1'
  },
  success: {
    background: '#ebf7ee',
    border: '1px solid #bfe5c8'
  },
  error: {
    background: '#fcedea',
    border: '1px solid #f6cac1'
  },
  warning: {
    background: '#fef7ea',
    border: '1px solid #ebce9c'
  },
  text: {
    fontSize: 14,
    fontWeight: 400,
    color: theme.colors.primaryBlack
  },
  icon: {
    background: '#0b56fd',
    width: 30,
    height: 30,
    borderRadius: 9,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    boxShadow:
      '0px -2.46203px 4.10338px 0px rgba(3, 3, 3, 0.10) inset, 0px 1.64135px 4px 0px rgb(0 0 0 / 19%)'
  },
  infoIcon: {
    background: '#0b56fd'
  },
  successIcon: {
    background: '#3fbf62'
  },
  errorIcon: {
    background: '#eb4e2d'
  },
  warningIcon: {
    background: '#ef9400'
  },
  leftItems: {
    display: 'flex',
    alignItems: 'center',
    gap: '17px'
  },
  cancelButton: {
    width: 30,
    height: 30,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#0000006e',
    transition: 'all 0.1s',
    '&:hover': {
      color: theme.colors.primaryBlack,
      background: '#5656561c'
    }
  }
}));

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export type ToastMessage = {
  variant: ToastVariant;
  text: string;
};

type Props = {
  message: ToastMessage;
  onClose?: () => void;
};

export default function Toast({ message, onClose }: Props) {
  const theme = useTheme();
  const classes = useStyles({ theme });

  function toastIcon(variant: ToastVariant): IconProp {
    switch (variant) {
      case 'success':
        return faCircleCheck;
      case 'info':
        return faCircleInfo;
      case 'error':
        return faCircleExclamation;
      case 'warning':
        return faTriangleExclamation;
    }
  }

  return (
    <div className={classNames(classes.base, classes[message.variant])}>
      <div className={classes.leftItems}>
        <div
          className={classNames(
            classes.icon,
            classes[message.variant + 'Icon']
          )}
        >
          <FontAwesomeIcon icon={toastIcon(message.variant)} />
        </div>
        <div className={classes.text}>{message.text}</div>
      </div>
      <div className={classes.cancelButton} onClick={onClose}>
        <FontAwesomeIcon icon={faTimes} />
      </div>
    </div>
  );
}
